import { z } from 'zod/v4';

/**
 * Box-shadow settings — matches `ShadowSettingsLegacy` from
 * `common/types/shadow-settings.type.ts` (the value emitted by the
 * `sp-shadow-settings` component).
 */
export const shadowSettingsSchemaLegacy = z
    .object({
        offsetX: z.number(),
        offsetY: z.number(),
        blur: z.number(),
        spread: z.number(),
        color: z.string(),
    })
    .describe('Box-shadow settings — only used when the shadow toggle is enabled.');

export type ShadowSettingsLegacy = z.infer<typeof shadowSettingsSchemaLegacy>;

/**
 * Shape fragment for nodes that carry the shadow toggle + settings pair
 * (button items today) — both `nullish()` because the pair post-dates the
 * widgets. Spread into the node's `z.object`. Form twin: `shadowPlugin` in
 * `@sites/entity-signal-form` (`plugins/widget/shadow.plugin.ts`).
 *
 * Not for figure: its data carries a bare **required** `isShadow` with no
 * settings bag, so it declares the field itself.
 */
export const shadowPluginShapeLegacy = {
    isShadow: z.boolean().nullish().describe('Whether the box-shadow effect is enabled.'),
    shadowSettings: shadowSettingsSchemaLegacy
        .nullish()
        .describe('Box-shadow settings (offsetX, offsetY, blur, spread, color) — only used when isShadow is true.'),
} as const;

/**
 * Default shadow bag. Mirrors `DEFAULT_SHADOW_SETTINGS` from the host type
 * module — used when a widget enables its shadow toggle without having a
 * stored settings object yet.
 */
export const createShadowSettingsLegacy = (overrides: Partial<ShadowSettingsLegacy> = {}): ShadowSettingsLegacy => ({
    offsetX: 0,
    offsetY: 0,
    blur: 12,
    spread: 4,
    color: 'rgba(0, 0, 0, .08)',
    ...overrides,
});
